import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import api from '../lib/api';

/**
 * Public landing page customers see AFTER paying via the Briefcase Pay
 * Checkout link. We poll the backend (which queries Stripe) until the
 * transaction reaches a terminal state.
 */
export function PaySuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState({ loading: true, status: null, amount: null, currency: 'USD' });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, status: 'no_session', amount: null, currency: 'USD' });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        // Note: this endpoint requires auth — but customers paying aren't logged in.
        // We just show a generic success page; webhook handles the real reconciliation server-side.
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/connect/transactions/status/${sessionId}`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            setState({ loading: false, status: data.status, amount: data.amount, currency: data.currency });
          }
          if (data.status === 'pending' && attempts < 8) {
            setTimeout(poll, 1500);
          }
        } else {
          // Customer isn't logged in (most common case): just show success
          if (!cancelled) setState({ loading: false, status: 'paid', amount: null, currency: 'USD' });
        }
      } catch (_) {
        if (!cancelled) setState({ loading: false, status: 'paid', amount: null, currency: 'USD' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.status === 'paid';

  return (
    <div className="bc-public min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        data-testid="pay-success-card"
        className="max-w-md w-full bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center shadow-2xl"
      >
        {state.loading ? (
          <>
            <Loader2 className="w-16 h-16 text-amber-700 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#12203e]">Confirmando pago…</h1>
          </>
        ) : paid ? (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-[#12203e] mb-2">¡Pago exitoso!</h1>
            {state.amount && (
              <p className="text-2xl font-bold text-amber-700 my-3">
                {new Intl.NumberFormat('es', { style: 'currency', currency: state.currency || 'USD' }).format(state.amount)}
              </p>
            )}
            <p className="text-slate-600 mb-6">
              Gracias por tu pago. Recibirás un comprobante por correo electrónico si proporcionaste tu email.
            </p>
            <Link
              to="/"
              data-testid="pay-success-home-btn"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-lg"
            >
              Volver al inicio <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 text-amber-700 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#12203e] mb-2">Procesando…</h1>
            <p className="text-slate-600">Tu banco está confirmando el pago. Puedes cerrar esta ventana.</p>
          </>
        )}
      </div>
    </div>
  );
}

export function PayCancelPage() {
  return (
    <div className="bc-public min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        data-testid="pay-cancel-card"
        className="max-w-md w-full bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center shadow-2xl"
      >
        <div className="w-20 h-20 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-12 h-12 text-rose-400" />
        </div>
        <h1 className="text-3xl font-bold text-[#12203e] mb-2">Pago cancelado</h1>
        <p className="text-slate-600 mb-6">
          No se realizó ningún cargo. Si quieres volver a intentarlo, pídele al comercio que te envíe un nuevo link de pago.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-bold px-5 py-2.5 rounded-lg"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}
